import { Button, DatePicker, Flex, Form, Select } from "antd";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import SelectDropdownLgIcon from "../../../../assets/icons/SelectDropdownLgIcon";
import CustomDrawer from "../../../../components/ui/CustomDrawer";
import Loader from "../../../../components/ui/Loader";
import { useAssignKpiGroupMutation } from "../../../../features/organization/contractual/JobDetails/jobDetailsApi";
import { useGetAllKPIGroupQuery } from "../../../../features/organization/contractual/KPISetup/kpiSetupApi";
import { useGetSingleJobAppliedQuery } from "../../../../features/organization/job/jobApi";
import useError from "../../../../hooks/useError";
import { disablePastDates } from "../../../../lib/time";
import { toast } from "../../../../lib/toast";
import CalanderIcon from "../../survey/templates/_icons/CalanderIcon";

export default function ManageAssignKpi({
  type,
  open,
  handleClose,
}: {
  type?: string;
  open: boolean;
  handleClose: (value?: boolean) => void;
}) {
  const [selectedGroup, setSelectedGroup] = useState<any>(null);
  const [form] = Form.useForm();
  const { id } = useParams();

  // ------ Queries ------
  const {
    data: allKpiGroupData,
    isLoading: allKpiGroupIsLoading,
    isError: allKpiGroupIsError,
    error: allKpiGroupError,
  } = useGetAllKPIGroupQuery({
    params: {
      page: 1,
      limit: 10000000000,
      status: "active",
      for: "assign-kip",
    },
  });

  const {
    data: singleJobAppliedData,
    isLoading: singleJobAppliedIsLoading,
    isError: singleJobAppliedIsError,
    error: singleJobAppliedError,
  } = useGetSingleJobAppliedQuery(Number(id), {
    skip: !id,
  });

  // ------ Mutations ------
  const [assignKpiGroup, { isLoading, isError, error, isSuccess }] =
    useAssignKpiGroupMutation();

  useError(allKpiGroupIsError, allKpiGroupError, "Failed to fetch KPI groups");
  useError(isError, error, "Failed to assigned KPI group");
  useError(
    singleJobAppliedIsError,
    singleJobAppliedError,
    "Failed to get single job applied data"
  );

  // --- Handle KPI group change
  // const handleGroupChange = (value: number | null) => {
  //   const group = allKpiGroupData?.data?.groups?.find(
  //     (item: any) => item.id === value
  //   );
  //   setSelectedGroup(group || null);

  //   // 🧹 Reset effective date when group changes
  //   form.setFieldsValue({ effective_date: null });
  // };

  // --- Disable previous dates based on effective_from
  // const disableBeforeEffectiveFrom = (current: any) => {
  //   if (!selectedGroup?.effective_from) return false;
  //   return (
  //     current && current < dayjs(selectedGroup.effective_from).startOf("day")
  //   );
  // };

  const onFinish = (values: any) => {
    const effectiveMonthWise = dayjs(values?.effective_date)
      .startOf("month")
      .format("YYYY-MM-DD");
    const effectiveDateWise = dayjs(values?.effective_date).format(
      "YYYY-MM-DD"
    );

    const newData = {
      kpi_group_id: values.kpi_group_id,
      effective_date:
        singleJobAppliedData?.data?.jobApplication?.active_contract
          ?.payment_type === "monthly"
          ? effectiveMonthWise
          : effectiveDateWise,
    };

    assignKpiGroup({ id: Number(id), data: newData });
  };

  useEffect(() => {
    if (isSuccess) {
      toast("success", "KPI assigned successfully");
      handleClose();
      form.resetFields();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuccess]);

  return (
    <>
      <CustomDrawer
        width={460}
        open={open}
        onClose={handleClose}
        title={"Assign Another KPI"}
      >
        {allKpiGroupIsLoading || singleJobAppliedIsLoading ? (
          <Loader />
        ) : (
          <Form
            form={form}
            size="large"
            layout="vertical"
            requiredMark="optional"
            className="custom-form"
            onFinish={onFinish}
          >
            {/* KPI Group */}
            <Form.Item
              name="kpi_group_id"
              label="Select KPI Group"
              className="mb-4"
              rules={[{ required: true, message: "Please select a KPI group" }]}
            >
              <Select
                placeholder="Select a Group"
                size="large"
                allowClear
                showSearch
                suffixIcon={<SelectDropdownLgIcon />}
                // onChange={handleGroupChange}
                options={
                  allKpiGroupData?.data?.groups?.map((item: any) => ({
                    label: item.group_name,
                    value: item.id,
                  })) || []
                }
              />
            </Form.Item>

            {/* Effective Date */}
            {singleJobAppliedData?.data?.jobApplication?.active_contract
              ?.payment_type === "monthly" ? (
              <Form.Item
                name="effective_date"
                label="Effective Month"
                className="mb-4"
                rules={[
                  { required: true, message: "Please select effective month" },
                ]}
              >
                <DatePicker
                  picker="month"
                  className="w-full"
                  format="MM/YYYY"
                  placeholder={"Select a month"}
                  suffixIcon={<CalanderIcon />}
                  // disabledDate={disableBeforeEffectiveFrom}
                  disabledDate={disablePastDates}
                />
              </Form.Item>
            ) : (
              <Form.Item
                name="effective_date"
                label="Effective Date"
                className="mb-4"
                rules={[
                  { required: true, message: "Please select effective date" },
                ]}
              >
                <DatePicker
                  className="w-full"
                  format="DD/MM/YYYY"
                  placeholder="Select a date"
                  suffixIcon={<CalanderIcon />}
                  // disabledDate={disableBeforeEffectiveFrom}
                  disabledDate={disablePastDates}
                />
              </Form.Item>
            )}

            <Flex align="center" gap={16} className="mt-6">
              <Button
                size="middle"
                htmlType="button"
                className="min-w-[120px] h-10"
                onClick={() => handleClose()}
              >
                Cancel
              </Button>
              <Button
                size="middle"
                htmlType="submit"
                type="primary"
                color="primary"
                className="min-w-[120px] h-10"
                loading={isLoading}
              >
                Assign KPI
              </Button>
            </Flex>
          </Form>
        )}
      </CustomDrawer>
    </>
  );
}
